import PropTypes from "prop-types";
import Avatar from "@shared/v2/Avatar";
import Typography from "@shared/v2/Typography";
import { ChevronRightSolidV6, CheckSolidV6 } from "@shared/v2/Icomoon";
import { useEffect, useState } from "react";
import TextButton from "@shared/v2/TextButton";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";
import ProgressRing from "./ProgressRing";

const DailyProgress = ({ completedDitlCards, completedPercentage, drawerOpen, onUndo }) => {
  const currentUser = useCurrentUser();
  const [showCompleted, setShowCompleted] = useState(false);
  const completedCount = completedDitlCards.length;
  const isComplete = completedPercentage >= 100;
  const fullName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ");

  useEffect(() => {
    if (!drawerOpen) setShowCompleted(false);
  }, [drawerOpen]);

  const toggleCompletedDitlCard = () => setShowCompleted(completedCount > 0 && !showCompleted);

  const handleUndo = (id) => {
    if (completedCount === 1) setShowCompleted(false);
    onUndo(id);
  };

  return (
    <div className="tw-flex tw-flex-col tw-relative tw-min-h-[52px] tw-px-[24px] tw-bg-white tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b-[1px] tw-border-neutral-gray-10 tw-drop-shadow-[0px_5px_8px_rgba(51,51,51,0.13)]">
      <div className="tw-flex tw-items-center tw-justify-between tw-min-h-[52px] tw-w-full">
        <div className="tw-flex tw-items-center tw-gap-[4px]">
          <Avatar size="small" src={currentUser?.avatar} alt={fullName} />
          <Typography variant="subtitle-s" component="span">
            {fullName}
          </Typography>
        </div>
        <TextButton
          schema="sentence"
          className="tw-flex tw-items-center tw-gap-[16px] tw-p-0"
          onClick={toggleCompletedDitlCard}
        >
          <span className="tw-flex tw-items-center tw-gap-[8px]">
            <Typography variant="subtitle-m" component="span">
              Completed {completedCount}
            </Typography>
            <span
              className={`tw-relative tw-flex tw-items-center tw-justify-center tw-w-[24px] tw-h-[24px] tw-rounded-full tw-transition-colors tw-duration-500 motion-reduce:tw-transition-none ${
                isComplete ? "tw-bg-semantic-green-5" : "tw-bg-transparent"
              }`}
            >
              <ProgressRing
                className="tw-absolute tw-inset-0"
                percentage={completedPercentage}
                size={24}
                strokeWidth={4.8}
                color={isComplete ? "var(--semantic-green-50)" : "var(--brand)"}
                trackColor="var(--tinted-gray-100, #ECEFEF)"
              />
              <CheckSolidV6
                className={`tw-w-[8px] tw-h-[8px] tw-text-semantic-green-110 tw-transition-opacity tw-duration-500 motion-reduce:tw-transition-none ${
                  isComplete ? "tw-opacity-100" : "tw-opacity-0"
                }`}
              />
            </span>
          </span>
          {completedCount > 0 && (
            <ChevronRightSolidV6
              className={`tw-w-[12px] tw-h-[12px] tw-text-[var(--brand)]
              ${showCompleted ? "tw-rotate-90" : ""}`}
            />
          )}
        </TextButton>
      </div>
      {showCompleted && (
        <div className="tw-flex tw-flex-col tw-gap-[12px] tw-pb-[12px] tw-max-h-[50vh] tw-overflow-y-auto">
          {completedDitlCards.map((card) => (
            <div key={card.id} className="tw-flex tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-[12px]">
                <CheckSolidV6 className="tw-text-gray-50" />
                <p className="tw-line-through tw-text-gray-50 tw-m-0">{card.name}</p>
              </div>
              <div className="tw-flex tw-gap-[6px]">
                <TextButton
                  className="tw-normal-case tw-font-normal tw-text-[12px]"
                  onClick={() => handleUndo(card.id)}
                >
                  Undo
                </TextButton>
                <p className="tw-m-0 tw-italic tw-text-[12px]">Complete</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

DailyProgress.propTypes = {
  completedDitlCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  completedPercentage: PropTypes.number.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  onUndo: PropTypes.func.isRequired,
};

export default DailyProgress;
